interface AsyncRequestCacheOptions {
  ttlMs: number;
  now?: () => number;
  shouldCache?: (value: unknown) => boolean;
}

interface GetRequestOptions {
  forceRefresh?: boolean;
}

interface CachedValue<T> {
  value: T;
  expiresAt: number;
}

interface InFlightRequest<T> {
  promise: Promise<T>;
  forceRefresh: boolean;
}

export const createAsyncRequestCache = <T>({
  ttlMs,
  now = Date.now,
  shouldCache = () => true,
}: AsyncRequestCacheOptions) => {
  const values = new Map<string, CachedValue<T>>();
  const requests = new Map<string, InFlightRequest<T>>();

  const get = (
    key: string,
    loader: () => Promise<T>,
    { forceRefresh = false }: GetRequestOptions = {},
  ): Promise<T> => {
    const activeRequest = requests.get(key);
    if (activeRequest && (!forceRefresh || activeRequest.forceRefresh)) {
      return activeRequest.promise;
    }

    const cachedValue = values.get(key);
    if (!forceRefresh && cachedValue && cachedValue.expiresAt > now()) {
      return Promise.resolve(cachedValue.value);
    }

    let sourceRequest: Promise<T>;
    try {
      sourceRequest = loader();
    } catch (error) {
      sourceRequest = Promise.reject(error);
    }

    let trackedRequest: Promise<T>;
    trackedRequest = sourceRequest
      .then((value) => {
        if (requests.get(key)?.promise === trackedRequest && shouldCache(value)) {
          values.set(key, { value, expiresAt: now() + ttlMs });
        }
        return value;
      })
      .finally(() => {
        if (requests.get(key)?.promise === trackedRequest) {
          requests.delete(key);
        }
      });

    requests.set(key, { promise: trackedRequest, forceRefresh });
    return trackedRequest;
  };

  const invalidate = (key: string) => {
    values.delete(key);
  };

  const clear = () => {
    values.clear();
  };

  return { get, invalidate, clear };
};
